/**
 * A question asked before an action that cannot be undone, such as cancelling a campaign or
 * unlinking an account: what the action does, and a button for each answer.
 *
 * The dialog is a native <dialog> opened with showModal(), which traps focus, dims the page and
 * closes on Escape. The button that keeps things as they are takes the focus first, so that a
 * stray Enter never confirms. Confirming closes the dialog at once. The page that asked shows the
 * action's progress and its outcome, as it does for any other action.
 */

import { type ReactNode, useEffect, useId, useRef } from "react";

import { styles } from "./ui.js";

export function ConfirmDialog({
  open,
  title,
  children,
  confirmLabel,
  cancelLabel,
  tone = "danger",
  confirmDisabled = false,
  onConfirm,
  onClose,
}: {
  /** Whether the dialog is shown. The page sets it, and `onClose` tells it the dialog closed. */
  open: boolean;
  /** The question, such as "Cancel the campaign?". */
  title: string;
  /** What the action does, and what it cannot undo. */
  children: ReactNode;
  /** The button that goes ahead, named after the action: "Cancel the campaign". */
  confirmLabel: string;
  /** The button that keeps things as they are: "Keep the campaign". */
  cancelLabel: string;
  /** "danger" for actions that give something up. "primary" for the others. */
  tone?: "danger" | "primary";
  /** Whether the action cannot go ahead yet, such as while a field in the dialog is incomplete. */
  confirmDisabled?: boolean;
  onConfirm: () => void;
  /** Called whenever the dialog closes: by either button, by Escape, or after confirming. */
  onClose: () => void;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const keep = useRef<HTMLButtonElement>(null);
  const titleId = useId();
  const textId = useId();

  useEffect(() => {
    const element = dialog.current;
    if (open && element && !element.open) {
      element.showModal();
      keep.current?.focus();
    } else if (!open && element?.open) {
      element.close();
    }
  }, [open]);

  function confirm() {
    onConfirm();
    dialog.current?.close();
  }

  return (
    <dialog
      ref={dialog}
      onClose={onClose}
      aria-labelledby={titleId}
      aria-describedby={textId}
      className="m-auto w-[calc(100%-2rem)] max-w-md rounded-3xl bg-white p-6 text-zinc-900 shadow-2xl ring-1 shadow-zinc-950/20 ring-zinc-900/10 backdrop:bg-ink-950/60 backdrop:backdrop-blur-md sm:p-7 dark:bg-ink-850 dark:text-zinc-100 dark:shadow-black/70 dark:ring-white/10 dark:backdrop:bg-black/60"
    >
      <h2 id={titleId} className="font-display text-lg font-semibold tracking-[-0.02em]">
        {title}
      </h2>
      <div
        id={textId}
        className="mt-3 space-y-2 text-sm leading-6 text-zinc-600 dark:text-zinc-400"
      >
        {children}
      </div>
      <div className="mt-6 flex flex-wrap justify-end gap-3">
        <button
          ref={keep}
          type="button"
          onClick={() => dialog.current?.close()}
          className={styles.secondaryButton}
        >
          {cancelLabel}
        </button>
        <button
          type="button"
          disabled={confirmDisabled}
          onClick={confirm}
          className={tone === "danger" ? styles.dangerButton : styles.primaryButton}
        >
          {confirmLabel}
        </button>
      </div>
    </dialog>
  );
}
